import { useCallback, useEffect, useRef, useState } from "react";
import { PinPad, PIN_MESSAGES } from "../components/PinPad";
import { Html5Qrcode } from "html5-qrcode";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Check, Gift, RotateCcw, AlertCircle, DollarSign, X, Camera, Smartphone } from "lucide-react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { unlockAudio, cueSuccess, cueRedeem, cueError, cueDuplicate } from "../lib/scanner_audio";

/** Pull a token out of whatever the camera decoded — plain string, `bp:{token}`,
 *  or any URL that carries `?token=` / `?t=`. */
function extractToken(raw) {
  if (!raw) return "";
  const s = raw.trim();
  if (s.startsWith("bp:")) return s.slice(3);
  try {
    const u = new URL(s);
    const t = u.searchParams.get("token") || u.searchParams.get("t");
    if (t) return t;
    // Wallet pass QRs encode {FRONTEND_URL}/stamp/{barcode_token} — pull the
    // token straight out of the path so the in-app camera reads real passes.
    const m = u.pathname.match(/\/stamp\/([A-Za-z0-9_-]+)/);
    if (m) return m[1];
  } catch (_) {}
  return s;
}

function typeLabel(t) {
  if (t === "points") return "points";
  if (t === "visit" || t === "membership") return "visits";
  return "stamps";
}

/** Map getUserMedia failures to instructions a barista can actually follow. */
function cameraErrorMessage(e) {
  const name = e?.name || "";
  if (name === "NotAllowedError" || name === "PermissionDeniedError")
    return "Camera access is blocked for this site. On iPhone: tap the \u201CAA\u201D or puzzle icon in Safari's address bar → Website Settings → Camera → Allow. Then tap Try again.";
  if (name === "NotFoundError" || name === "OverconstrainedError")
    return "No camera was found on this device.";
  if (name === "NotReadableError" || name === "TrackStartError")
    return "The camera is busy in another app. Close it and tap Try again.";
  return e?.message || "The camera couldn't start on this device.";
}

/**
 * Scanner — in-app camera for a signed-in till device (dashboard session).
 * Camera never auto-starts blind: if permission is already granted we start
 * immediately; otherwise a "Start camera" tap raises the prompt (iOS Safari).
 * Reward-available and points programs pause for confirmation. Undo within 3 min.
 */
export default function Scanner({ backHref = "/app", onBack = null }) {
  const authCtx = useAuth();
  const activeOrg = authCtx?.activeOrg;
  const nav = useNavigate();

  const [camState, setCamState] = useState("idle"); // idle | starting | running | error
  const [camError, setCamError] = useState(null);
  const [flash, setFlash] = useState(null); // { kind: "ok"|"err", ... }
  const [pending, setPending] = useState(null); // { holder, program }
  const [pinError, setPinError] = useState(null); // redemption PIN feedback
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const containerId = useRef("qr-scan-container").current;
  const scannerRef = useRef(null);
  const busyRef = useRef(false);
  const pausedRef = useRef(false);
  const lastRef = useRef({ token: null, at: 0 });
  const lastActionRef = useRef(null);

  useEffect(() => { pausedRef.current = !!(flash || pending || busy); }, [flash, pending, busy]);

  const apiClient = useRef({
    lookupBarcode: async (barcode) => (await api.get(`/scan/${encodeURIComponent(barcode)}`)).data,
    commit: async (body) => (await api.post(`/orgs/${activeOrg?.id}/scan/action`, body)).data,
    reverse: async (key) => (await api.post(`/orgs/${activeOrg?.id}/scan/action/reverse`, { idempotency_key: key })).data,
  }).current;

  const stampAction = useCallback((program, holder) => {
    if (holder.reward_status === "available") return "redeem";
    if (program.type === "points") return "add_points";
    if (program.type === "visit" || program.type === "membership") return "add_visit";
    return "add_stamp";
  }, []);

  const commit = useCallback(async ({ holder, program, action, amt = 0, pin = null }) => {
    unlockAudio();
    busyRef.current = true; setBusy(true);
    try {
      const key = `${holder.id}-${action}-${holder.balance}-${holder.visits || 0}-${holder.points || 0}`;
      const data = await apiClient.commit({
        token: holder.barcode_token, action, amount: amt, idempotency_key: key, pin,
      });
      setPinError(null);
      if (data.duplicate) cueDuplicate();
      else if (action === "redeem" || data.reward_unlocked) cueRedeem();
      else cueSuccess();
      lastActionRef.current = { holder_id: holder.id, action, key };
      setFlash({
        kind: "ok",
        holder: data.holder || holder,
        program,
        change: action === "redeem" ? "redeem" : "+1",
        rewardUnlocked: !!data.reward_unlocked,
        duplicate: !!data.duplicate,
      });
      setPending(null); setAmount("");
      setTimeout(() => setFlash((f) => (f && f.holder.id === (data.holder || holder).id ? null : f)), 1700);
    } catch (e) {
      cueError();
      const code = e?.response?.data?.detail;
      if (PIN_MESSAGES[code]) { setPinError(PIN_MESSAGES[code]); return; }
      setFlash({ kind: "err", message: apiError(e) });
      setTimeout(() => setFlash(null), 1800);
    } finally {
      busyRef.current = false; setBusy(false);
    }
  }, [apiClient]);

  const undo = useCallback(async () => {
    const last = lastActionRef.current;
    if (!last) return;
    try {
      setBusy(true); busyRef.current = true;
      await apiClient.reverse(last.key);
      toast.success("Undone");
      setFlash(null);
      lastActionRef.current = null;
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false); busyRef.current = false;
    }
  }, [apiClient]);

  /** Shared routing after a lookup: reward → pause, points → pause, else auto-stamp. */
  const routeAfterLookup = useCallback(async (holder, program) => {
    if (holder.status === "blocked") {
      setFlash({ kind: "err", message: "This pass is blocked." });
      setTimeout(() => setFlash(null), 1600);
      return;
    }
    if (holder.reward_status === "available" || program.type === "points") {
      setPending({ holder, program });
      return;
    }
    await commit({ holder, program, action: stampAction(program, holder) });
  }, [commit, stampAction]);

  /** Camera decode → same route. */
  const onDecoded = useCallback(async (decoded) => {
    if (pausedRef.current || busyRef.current) return;
    const token = extractToken(decoded);
    if (!token) return;
    const now = Date.now();
    if (lastRef.current.token === token && now - lastRef.current.at < 4000) return;
    lastRef.current = { token, at: now };
    busyRef.current = true; setBusy(true);
    try {
      const data = await apiClient.lookupBarcode(token);
      busyRef.current = false; setBusy(false);
      await routeAfterLookup(data.holder, data.program);
    } catch (e) {
      setFlash({ kind: "err", message: apiError(e) });
      setTimeout(() => setFlash(null), 1600);
      busyRef.current = false; setBusy(false);
    }
  }, [apiClient, routeAfterLookup]);

  const stopCamera = useCallback(async () => {
    const s = scannerRef.current;
    scannerRef.current = null;
    if (s) {
      try { await s.stop(); } catch (_) {}
      try { s.clear(); } catch (_) {}
    }
  }, []);

  /** Called from a real tap (or auto when permission is already granted). */
  const startCamera = useCallback(async () => {
    if (scannerRef.current) return;
    unlockAudio();
    setCamState("starting"); setCamError(null);
    try {
      const html5 = new Html5Qrcode(containerId, { verbose: false });
      scannerRef.current = html5;
      await html5.start(
        { facingMode: { ideal: "environment" } },
        { fps: 12,
          qrbox: (w, h) => {
            const min = Math.min(w, h);
            const box = Math.floor(min * 0.68);
            return { width: box, height: box };
          },
          disableFlip: false,
        },
        onDecoded,
        () => {},
      );
      setCamState("running");
    } catch (e) {
      scannerRef.current = null;
      setCamError(cameraErrorMessage(e));
      setCamState("error");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onDecoded]);

  // On mount: auto-start ONLY when the browser has already granted camera
  // permission. First-time users get the tap-to-start button so the
  // permission prompt fires inside a user gesture (iOS Safari requirement).
  useEffect(() => {
    if (!activeOrg) return;
    let cancelled = false;
    (async () => {
      try {
        const st = await navigator.permissions.query({ name: "camera" });
        if (!cancelled && st.state === "granted") startCamera();
      } catch (_) { /* permissions API unsupported → wait for the tap */ }
    })();
    return () => { cancelled = true; stopCamera(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrg?.id]);

  if (!activeOrg) return null;

  const orgName = activeOrg.name;
  const goBack = () => (onBack ? onBack() : nav(backHref));

  return (
    <div className="fixed inset-0 z-40 flex flex-col bg-black" data-testid="scanner-live">
      {/* Camera video mount point — must exist before html5-qrcode starts */}
      <div id={containerId} className="absolute inset-0 [&_video]:!w-full [&_video]:!h-full [&_video]:!object-cover bg-black" />

      {/* Top bar */}
      <div className="relative z-30 flex items-center justify-between px-4 pt-[max(1rem,env(safe-area-inset-top))] pb-3 pointer-events-none">
        <button
          onClick={goBack}
          data-testid="scanner-back"
          className="h-10 w-10 rounded-full bg-white/12 backdrop-blur-md hover:bg-white/20 grid place-items-center text-white transition pointer-events-auto"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="text-white/85 text-[11.5px] font-semibold tracking-[0.18em] uppercase truncate max-w-[55vw]">
          {orgName}
        </div>
        <div className="h-10 w-10" />
      </div>

      {/* Tap-to-start — the user gesture iOS needs for the permission prompt */}
      {camState === "idle" && !flash && !pending && (
        <div className="absolute inset-0 grid place-items-center px-6 z-20">
          <div className="max-w-sm w-full text-center" data-testid="scanner-idle">
            <div className="h-20 w-20 rounded-3xl bg-white/[0.08] border border-white/15 grid place-items-center mx-auto mb-6">
              <Camera size={32} className="text-white/85" strokeWidth={2} />
            </div>
            <div className="font-display font-extrabold text-white text-2xl tracking-[-0.02em]">In-app scanner</div>
            <div className="text-white/60 text-[13.5px] mt-2 leading-relaxed">
              Your browser will ask for camera access the first time — tap Allow.
            </div>
            <button
              onClick={startCamera}
              data-testid="scanner-start-camera"
              className="mt-6 w-full h-14 rounded-2xl bg-[#C8FF4D] text-[#0B0D10] font-semibold text-[15px] inline-flex items-center justify-center gap-2 hover:bg-[#b8ef3d] active:scale-[0.98] transition shadow-[0_16px_44px_-10px_rgba(200,255,77,0.45)]"
            >
              <Camera size={17} strokeWidth={2.4} />Start camera
            </button>
            <div className="mt-6 rounded-2xl bg-white/[0.05] border border-white/10 px-4 py-3.5 text-left flex gap-3 items-start">
              <Smartphone size={16} className="text-[#C8FF4D] mt-0.5 shrink-0" />
              <div className="text-white/55 text-[12.5px] leading-relaxed">
                <span className="text-white/80 font-semibold">Staff phones:</span> point the normal camera app at the customer's pass and enter the staff PIN. No login needed.
              </div>
            </div>
          </div>
        </div>
      )}

      {camState === "starting" && (
        <div className="absolute inset-0 grid place-items-center z-10 pointer-events-none">
          <div className="text-white/60 text-[12.5px] tracking-[0.18em] uppercase animate-pulse">Starting camera…</div>
        </div>
      )}

      {/* Running: reticle + hint */}
      {camState === "running" && (
        <>
          {!flash && !pending && (
            <div className="absolute inset-0 grid place-items-center pointer-events-none">
              <div className="relative" style={{ width: "min(72vw, 320px)", height: "min(72vw, 320px)" }}>
                {[["top-0 left-0","border-t-2 border-l-2 rounded-tl-2xl"],["top-0 right-0","border-t-2 border-r-2 rounded-tr-2xl"],["bottom-0 left-0","border-b-2 border-l-2 rounded-bl-2xl"],["bottom-0 right-0","border-b-2 border-r-2 rounded-br-2xl"]].map(([pos, cls]) => (
                  <span key={pos} className={`absolute ${pos} h-10 w-10 ${cls} border-[#C8FF4D]`} />
                ))}
                <motion.div className="absolute inset-x-2 h-[2px] bg-gradient-to-r from-transparent via-[#C8FF4D] to-transparent"
                  animate={{ y: [0, "88%", 0] }} transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }} />
              </div>
            </div>
          )}
          {!flash && !pending && (
            <div className="absolute inset-x-0 bottom-[max(1.25rem,env(safe-area-inset-bottom))] z-10 text-center text-white/60 text-[12.5px] pointer-events-none">
              Records automatically the moment the QR is in frame
            </div>
          )}
        </>
      )}

      {/* Camera error — clear fix steps + retry + the native-camera way out */}
      {camState === "error" && (
        <div className="absolute inset-0 grid place-items-center px-6 z-20">
          <div className="max-w-sm w-full rounded-2xl bg-white/[0.08] backdrop-blur-md border border-white/15 p-6 text-white text-center" data-testid="scanner-error">
            <AlertCircle size={22} className="text-[#FF7A3D] mx-auto mb-3" />
            <div className="font-display font-bold text-lg mb-1.5">Camera couldn't start</div>
            <div className="text-white/70 text-[13px] leading-relaxed">{camError}</div>
            <button onClick={startCamera} data-testid="scanner-retry-camera" className="mt-4 w-full h-12 rounded-2xl bg-[#C8FF4D] text-[#0B0D10] text-[13.5px] font-semibold inline-flex items-center justify-center gap-1.5 hover:bg-[#b8ef3d] transition">
              <Camera size={14} strokeWidth={2.4} />Try again
            </button>
            <div className="mt-4 rounded-xl bg-white/[0.05] border border-white/10 px-4 py-3 text-left flex gap-2.5 items-start">
              <Smartphone size={14} className="text-[#C8FF4D] mt-0.5 shrink-0" />
              <div className="text-white/55 text-[12px] leading-relaxed">
                No luck? Use any phone's normal camera app on the customer's pass and enter the staff PIN.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Auto-stamp flash overlay */}
      <AnimatePresence>
        {flash && flash.kind === "ok" && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.18 }}
            className="absolute inset-0 z-50 grid place-items-center px-6" data-testid="scanner-flash-ok"
          >
            <div className="absolute inset-0 bg-[#0B0D10]/75 backdrop-blur-[3px]" />
            <motion.div
              initial={{ scale: 0.75, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", damping: 18, stiffness: 260 }}
              className="relative bg-[#C8FF4D] text-[#0B0D10] rounded-[32px] px-8 py-8 text-center shadow-[0_30px_80px_-10px_rgba(200,255,77,0.5)] min-w-[280px] max-w-[92vw]"
            >
              {flash.duplicate ? (
                <>
                  <div className="text-[11px] uppercase tracking-[0.22em] font-semibold opacity-70">Already counted</div>
                  <div className="font-display font-extrabold tracking-[-0.02em] leading-[0.9] mt-2" style={{ fontSize: "clamp(2rem,6vw,2.6rem)" }}>No change</div>
                  <div className="text-[13px] mt-2 opacity-70">{flash.holder.pass_number} · {flash.holder.balance}/{(flash.program.reward?.threshold) || 10}</div>
                </>
              ) : flash.change === "redeem" ? (
                <>
                  <Gift size={44} strokeWidth={2.4} className="mx-auto mb-2" />
                  <div className="text-[11px] uppercase tracking-[0.22em] font-semibold opacity-70">Redeemed</div>
                  <div className="font-display font-extrabold tracking-[-0.02em] leading-[0.9] mt-1" style={{ fontSize: "clamp(2rem,7vw,2.8rem)" }}>{flash.program.reward?.name || "Reward"}</div>
                  <div className="text-[13px] mt-2 opacity-70">{flash.holder.pass_number}</div>
                </>
              ) : (
                <>
                  <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold opacity-70">
                    <Check size={13} strokeWidth={3} />Stamped
                  </div>
                  <div className="font-display font-extrabold tracking-[-0.02em] leading-[0.85] mt-2" style={{ fontSize: "clamp(3.4rem, 14vw, 5.4rem)" }}>
                    {flash.holder.balance}<span className="opacity-40">/{(flash.program.reward?.threshold) || 10}</span>
                  </div>
                  <div className="text-[13px] mt-2 opacity-70">{flash.holder.pass_number} · {typeLabel(flash.program.type)}</div>
                  {flash.rewardUnlocked && (
                    <div className="inline-flex items-center gap-1.5 mt-3 bg-[#0B0D10] text-[#C8FF4D] rounded-full px-3 py-1 text-[12px] font-semibold">
                      <Gift size={13} />Reward unlocked
                    </div>
                  )}
                </>
              )}
              {!flash.duplicate && flash.change !== "redeem" && (
                <button
                  onClick={undo} disabled={busy} data-testid="scanner-undo"
                  className="mt-5 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#0B0D10]/70 hover:text-[#0B0D10] transition"
                >
                  <RotateCcw size={13} />Undo
                </button>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error flash */}
      <AnimatePresence>
        {flash && flash.kind === "err" && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 z-50 grid place-items-center px-6" data-testid="scanner-flash-err"
          >
            <div className="absolute inset-0 bg-black/72 backdrop-blur-[2px]" />
            <motion.div
              initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", damping: 22, stiffness: 260 }}
              className="relative bg-white text-[#0B0D10] rounded-[28px] px-7 py-6 text-center max-w-[92vw]"
            >
              <AlertCircle size={26} className="text-[#FF7A3D] mx-auto mb-2" />
              <div className="font-display font-bold text-lg">Try again</div>
              <div className="text-[13px] text-[#25282E]/70 mt-1 max-w-[32ch]">{flash.message || "That pass wasn't recognised."}</div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Pending — reward / points confirmation */}
      <AnimatePresence>
        {pending && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 z-50 grid place-items-end sm:place-items-center px-4 pb-[max(1rem,env(safe-area-inset-bottom))]"
            data-testid="scanner-pending"
          >
            <div className="absolute inset-0 bg-black/72 backdrop-blur-[2px]" onClick={() => !busy && (setPending(null), setAmount(""))} />
            <motion.div
              initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              transition={{ type: "spring", damping: 28, stiffness: 240 }}
              className="relative bg-white text-[#0B0D10] rounded-[28px] px-6 pt-6 pb-6 w-full max-w-md mx-auto"
            >
              <div className="text-[10.5px] uppercase tracking-[0.2em] font-semibold text-[#25282E]/55">{pending.program.name}</div>
              <div className="font-display font-extrabold tracking-[-0.02em] text-2xl mt-1">{pending.holder.pass_number}</div>
              <div className="mt-4 rounded-2xl bg-[#0B0D10] text-white text-center py-5 relative overflow-hidden">
                <div className="absolute -top-6 right-2 h-24 w-24 rounded-full bg-[#C8FF4D]/15 blur-2xl" />
                <div className="relative z-10">
                  <div className="font-display font-extrabold leading-none" style={{ fontSize: "clamp(2.4rem, 10vw, 3.4rem)" }}>
                    {pending.holder.balance}<span className="text-white/40 text-2xl">/{(pending.program.reward?.threshold) || 10}</span>
                  </div>
                  <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/55 mt-1.5">{typeLabel(pending.program.type)}</div>
                </div>
              </div>

              {pending.holder.reward_status === "available" ? (
                <>
                  <div className="mt-4 rounded-xl bg-[#C8FF4D]/15 border border-[#C8FF4D]/30 px-4 py-3 flex items-center gap-2.5">
                    <Gift size={17} className="text-[#0B0D10]" />
                    <div className="text-[13px] font-medium text-[#0B0D10]">
                      Reward ready: <span className="font-semibold">{pending.program.reward?.name || "Reward"}</span>
                    </div>
                  </div>
                  <div className="mt-4 space-y-2">
                    {pending.program.redeem_pin_required && (
                      <div className="rounded-2xl bg-[#0B0D10] p-4 mb-3" data-testid="scanner-pin-step">
                        <div className="text-[11px] uppercase tracking-[0.2em] text-white/55 font-semibold text-center mb-3">Customer enters PIN to redeem</div>
                        <PinPad onComplete={(pin) => commit({ holder: pending.holder, program: pending.program, action: "redeem", pin })} error={pinError} disabled={busy} testid="scanner-pin" />
                      </div>
                    )}
                    <button onClick={() => commit({ holder: pending.holder, program: pending.program, action: "redeem" })} disabled={busy || pending.program.redeem_pin_required} data-testid="scanner-redeem" className={`w-full bg-[#C8FF4D] text-[#0B0D10] rounded-2xl h-14 font-semibold text-[15px] inline-flex items-center justify-center gap-2 hover:bg-[#b8ef3d] active:scale-[0.98] transition disabled:opacity-50 ${pending.program.redeem_pin_required ? "hidden" : ""}`}>
                      <Gift size={17} strokeWidth={2.4} />Redeem
                    </button>
                    <button onClick={() => commit({ holder: pending.holder, program: pending.program, action: pending.program.type === "points" ? "add_points" : (pending.program.type === "visit" || pending.program.type === "membership" ? "add_visit" : "add_stamp") })} disabled={busy} data-testid="scanner-add-instead" className="w-full bg-white text-[#0B0D10] border border-[#0B0D10]/15 rounded-2xl h-12 font-semibold text-[13.5px] hover:bg-[#F4F1E9] transition disabled:opacity-50">
                      Not redeeming · just add another
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="mt-4">
                    <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#25282E]/55 mb-2">Amount spent</div>
                    <div className="relative">
                      <DollarSign size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#25282E]/40" />
                      <input autoFocus type="number" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className="w-full bg-[#F4F1E9] border border-black/8 rounded-2xl h-14 pl-10 pr-4 text-[18px] font-display font-bold focus:outline-none focus:border-black/25" data-testid="scanner-points-amount" />
                    </div>
                  </div>
                  <button onClick={() => commit({ holder: pending.holder, program: pending.program, action: "add_points", amt: parseFloat(amount) || 0 })} disabled={busy || !amount} data-testid="scanner-add-points" className="mt-4 w-full bg-[#0B0D10] text-white rounded-2xl h-14 font-semibold text-[15px] hover:bg-[#25282E] active:scale-[0.98] transition disabled:opacity-40">
                    Add {amount || "0"} × ${(pending.program.points_per_dollar || 1)} points
                  </button>
                </>
              )}
              <button onClick={() => { setPending(null); setAmount(""); setPinError(null); }} disabled={busy} data-testid="scanner-pending-cancel" className="mt-3 w-full h-10 text-[13px] text-[#25282E]/55 hover:text-[#0B0D10] transition inline-flex items-center justify-center gap-1.5">
                <X size={14} />Cancel
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
